import { useEffect, useState, FormEvent } from "react";
import { FiUser, FiActivity, FiCalendar, FiCheckCircle } from "react-icons/fi";
import { toast } from "react-toastify";
import linksthree from "../../services/linksthree";
import utils from "../../helpers/utils";
import ScheduleModalHeader from "./ScheduleModalHeader";
import SuccessScreen from "./SuccessScreen";
import PersonalDataStep from "./PersonalDataStep";
import ActivityStep from "./ActivityStep";
import DateTimeStep from "./DateTimeStep";
import ReviewStep from "./ReviewStep";
import FooterActions from "./FooterActions";

interface LinkThreeScheduleModalProps {
  isOpen: boolean;
  professionalId?: string | number | null;
  professionalName?: string | null;
  primaryColor?: string | null;
  secondaryColor?: string | null;
  backgroundColor?: string | null;
  onClose: () => void;
}

const initialForm = {
  first_name: "",
  email: "",
  phone: "",
  type: "",
  date_time: utils.toLocalISOString(new Date()),
  duration_minutes: 0,
};

type Step = 1 | 2 | 3 | 4;

function LinkThreeScheduleModal({
  isOpen,
  professionalId = null,
  professionalName,
  primaryColor,
  secondaryColor,
  backgroundColor,
  onClose,
}: LinkThreeScheduleModalProps) {
  const [currentStep, setCurrentStep] = useState<Step>(1);
  const [form, setForm] = useState(initialForm);
  const [selectedDate, setSelectedDate] = useState<string>(
    utils.toLocalISOString(new Date())
  );
  const [timeInput, setTimeInput] = useState(
    utils.toLocalISOString(new Date()).slice(11, 16)
  );
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [timeOptions, setTimeOptions] = useState<string[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [services, setServices] = useState<
    Array<{ id: number; name: string; duration_minutes: number }>
  >([]);

  const accentColor = primaryColor || "#9333ea";
  const textColor = secondaryColor || "#FFFFFF";

  const steps = [
    { number: 1, label: "Dados", icon: FiUser },
    { number: 2, label: "Atividade", icon: FiActivity },
    { number: 3, label: "Data/Hora", icon: FiCalendar },
    { number: 4, label: "Revisar", icon: FiCheckCircle },
  ];

  const loadServices = async () => {
    if (!professionalId) return;

    try {
      const data = await linksthree.getServices(professionalId);
      setServices(data || []);
    } catch (error) {
      console.error("Erro ao carregar serviços:", error);
      setServices([]);
    }
  };

  useEffect(() => {
    if (isOpen) {
      const now = utils.toLocalISOString(new Date());
      const time = now.slice(11, 16);
      setForm({
        ...initialForm,
        date_time: now,
      });
      setSelectedDate(now);
      setTimeInput(time);
      setCurrentStep(1);
      setLoading(false);
      setSuccess(false);
      loadServices();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, professionalId]);

  useEffect(() => {
    const hasValidTime = /^([01]\d|2[0-3]):([0-5]\d)$/.test(timeInput);
    if (!selectedDate || !hasValidTime) {
      return;
    }
    const datePart = utils
      .toLocalISOString(new Date(selectedDate))
      .slice(0, 10);
    const combined = `${datePart}T${timeInput}`;
    setForm((prev) => ({
      ...prev,
      date_time: combined,
    }));
  }, [selectedDate, timeInput]);

  const handleChange = (target: { name: string; value: string }) => {
    if (target.name === "phone") {
      // Remove todos os caracteres não numéricos
      const digitsOnly = target.value.replace(/\D/g, "");
      setForm((prev) => ({
        ...prev,
        [target.name]: digitsOnly,
      }));
      return;
    }

    setForm((prev) => ({
      ...prev,
      [target.name]: target.value,
    }));
  };

  // Buscar horários disponíveis quando a data ou duração mudarem
  useEffect(() => {
    if (!professionalId || !selectedDate) {
      setTimeOptions([]);
      return;
    }

    const duration =
      form.duration_minutes && form.duration_minutes > 0
        ? form.duration_minutes
        : 30; // Default de 30 minutos

    const fetchAvailableSlots = async () => {
      setLoadingSlots(true);
      try {
        const dateStr = selectedDate.slice(0, 10); // YYYY-MM-DD
        const slots = await linksthree.getAvailableSlots(
          professionalId,
          dateStr,
          duration
        );
        setTimeOptions(slots);

        // Se o horário atual não está mais disponível, resetar
        if (timeInput && !slots.includes(timeInput)) {
          setTimeInput(slots[0] || "");
        }
      } catch (error) {
        console.error("Erro ao buscar horários disponíveis:", error);
        setTimeOptions([]);
      } finally {
        setLoadingSlots(false);
      }
    };

    fetchAvailableSlots();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [professionalId, selectedDate, form.duration_minutes]);

  const canGoToNextStep = (): boolean => {
    switch (currentStep) {
      case 1:
        return (
          !!form.first_name.trim() && !!form.email.trim() && !!form.phone.trim()
        );
      case 2:
        return !!form.type;
      case 3:
        return !!selectedDate && /^([01]\d|2[0-3]):([0-5]\d)$/.test(timeInput);
      case 4:
        // Step 4 é revisão, todos os campos já foram validados nos steps anteriores
        return true;
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (!canGoToNextStep()) {
      toast.error("Preencha todos os campos obrigatórios");
      return;
    }
    if (currentStep < 4) {
      setCurrentStep((prev) => (prev + 1) as Step);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => (prev - 1) as Step);
    }
  };

  const handleDateChange = (date: string) => {
    const selectedDateObj = new Date(date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    selectedDateObj.setHours(0, 0, 0, 0);

    // Se a data selecionada for anterior a hoje, usar a data de hoje
    if (selectedDateObj < today) {
      const todayISO = utils.toLocalISOString(new Date());
      setSelectedDate(todayISO);
      toast.warning("Não é possível selecionar datas anteriores a hoje");
      return;
    }

    setSelectedDate(date);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!professionalId) {
      toast.error("Profissional não encontrado para agendamento.");
      return;
    }

    if (!canGoToNextStep()) {
      toast.error("Preencha todos os campos obrigatórios.");
      return;
    }

    setLoading(true);

    const payload = {
      first_name: form.first_name.trim(),
      last_name: "",
      email: form.email.trim(),
      phone: form.phone.trim(),
      type: form.type,
      date_time: form.date_time.slice(0, 16),
      duration_minutes: form.duration_minutes,
    };

    const success = await linksthree.scheduleAppointment(
      professionalId,
      payload
    );

    setLoading(false);

    if (success) {
      toast.success("Agendamento realizado com sucesso!");
      setSuccess(true);
    } else {
      toast.error("Não foi possível realizar o agendamento.");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[999999] flex items-end sm:items-center justify-center bg-black bg-opacity-60 p-0 sm:p-4">
      <div className="relative flex h-[99vh] sm:h-[99vh] w-full max-w-2xl flex-col overflow-hidden rounded-none sm:rounded-2xl border-t border-stroke bg-white shadow-2xl dark:border-strokedark dark:bg-boxdark sm:border">
        <ScheduleModalHeader
          title={
            success
              ? "Confirmação"
              : `Agendar com ${professionalName || "profissional"}`
          }
          subtitle={
            success
              ? "Agendamento confirmado"
              : `Passo ${currentStep} de ${steps.length}`
          }
          currentStep={currentStep}
          totalSteps={steps.length}
          steps={steps}
          accentColor={accentColor}
          textColor={textColor}
          backgroundColor={backgroundColor || undefined}
          showStepper={!success}
          onClose={onClose}
        />

        {success ? (
          <SuccessScreen
            email={form.email}
            accentColor={accentColor}
            textColor={textColor}
            onClose={onClose}
          />
        ) : (
          <form
            onSubmit={handleSubmit}
            className="flex flex-1 flex-col overflow-hidden"
          >
            <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-6">
              {currentStep === 1 && (
                <PersonalDataStep
                  form={form}
                  accentColor={accentColor}
                  onChange={handleChange}
                />
              )}

              {currentStep === 2 && (
                <ActivityStep
                  selectedType={form.type}
                  accentColor={accentColor}
                  services={services}
                  onSelect={(type, durationMinutes) => {
                    setForm((prev) => ({
                      ...prev,
                      type,
                      duration_minutes: durationMinutes,
                    }));
                  }}
                />
              )}

              {currentStep === 3 && (
                <DateTimeStep
                  selectedDate={selectedDate}
                  selectedTime={timeInput}
                  timeOptions={timeOptions}
                  loadingSlots={loadingSlots}
                  accentColor={accentColor}
                  backgroundColor={backgroundColor || undefined}
                  onDateChange={handleDateChange}
                  onTimeChange={setTimeInput}
                />
              )}

              {currentStep === 4 && (
                <ReviewStep
                  form={form}
                  selectedDate={selectedDate}
                  selectedTime={timeInput}
                  accentColor={accentColor}
                />
              )}
            </div>

            <FooterActions
              currentStep={currentStep}
              totalSteps={steps.length}
              canGoNext={canGoToNextStep()}
              loading={loading}
              accentColor={accentColor}
              textColor={textColor}
              backgroundColor={backgroundColor || undefined}
              onBack={handleBack}
              onNext={handleNext}
            />
          </form>
        )}
      </div>

      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }

        /* Melhorias para scroll suave em mobile */
        @media (max-width: 640px) {
          .overflow-y-auto {
            -webkit-overflow-scrolling: touch;
          }
        }
      `}</style>
    </div>
  );
}

export default LinkThreeScheduleModal;
